import { FC, useMemo, useState, useCallback } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { Loader2 } from "lucide-react"

import Accordion from "../../accordion"
import { useFetchSubscriptionCountQuery } from "../../../../redux/queries"
import { groupSubscriptionCountByMonth } from "../../../utils/subscription"
import {
  getStackedColumnChartOptions,
  ChartSeries
} from "../../charts/stacked-column-chart-options"

export interface ChartClickData {
  period: string
  viewType: "daily" | "monthly"
  status: "active" | "disabled" | "deleted"
  category: string
  index: number
}

interface Props {
  orgId?: string
  loid?: string
  onChartClick?: (data: ChartClickData) => void
  selectedBarIndex?: number | null
}

type ViewType = "daily" | "monthly"

const SubscriptionStats: FC<Props> = ({
  orgId,
  loid,
  onChartClick,
  selectedBarIndex
}) => {
  const [viewType, setViewType] = useState<ViewType>("monthly")

  // Determine range based on view type
  const range = viewType === "daily" ? 90 : 360

  // Fetch data for each status
  // Try active_only first, fallback to active=1&deleted=0
  const { data: activeSubscriptions = [], isFetching: isFetchingActive } =
    useFetchSubscriptionCountQuery({
      range,
      org_id: orgId,
      loid,
      active: 1,
      deleted: 0
    })

  const { data: disabledSubscriptions = [], isFetching: isFetchingDisabled } =
    useFetchSubscriptionCountQuery({
      range,
      org_id: orgId,
      loid,
      active: 0,
      deleted: 0
    })

  const { data: deletedSubscriptions = [], isFetching: isFetchingDeleted } =
    useFetchSubscriptionCountQuery({
      range,
      org_id: orgId,
      loid,
      deleted: 1
    })

  const isLoading = isFetchingActive || isFetchingDisabled || isFetchingDeleted

  // Log API responses for debugging
  useMemo(() => {
    if (!isLoading) {
      console.log("[SubscriptionStats] API Responses:", {
        active: activeSubscriptions,
        disabled: disabledSubscriptions,
        deleted: deletedSubscriptions,
        viewType,
        range
      })
    }
  }, [
    activeSubscriptions,
    disabledSubscriptions,
    deletedSubscriptions,
    isLoading,
    viewType,
    range
  ])

  // Transform data for daily view
  const dailyChartData = useMemo(() => {
    if (isLoading || viewType !== "daily") return null

    // Create maps for quick lookups
    const activeMap = new Map(
      activeSubscriptions.map(item => [
        item.period_start,
        item.subscription_count
      ])
    )
    const disabledMap = new Map(
      disabledSubscriptions.map(item => [
        item.period_start,
        item.subscription_count
      ])
    )
    const deletedMap = new Map(
      deletedSubscriptions.map(item => [
        item.period_start,
        item.subscription_count
      ])
    )

    // Get all unique dates and sort them
    const allDates = Array.from(
      new Set([
        ...activeSubscriptions.map(item => item.period_start),
        ...disabledSubscriptions.map(item => item.period_start),
        ...deletedSubscriptions.map(item => item.period_start)
      ])
    ).sort()

    // Build arrays for chart
    const categories = allDates.map(date => {
      // Format date as "MMM DD" (e.g., "Jan 15")
      const dateObj = new Date(date)
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      })
    })

    const activeData = allDates.map(date => activeMap.get(date) || 0)
    const disabledData = allDates.map(date => disabledMap.get(date) || 0)
    const deletedData = allDates.map(date => deletedMap.get(date) || 0)

    return {
      categories,
      activeData,
      disabledData,
      deletedData,
      periods: allDates // Store original ISO date strings
    }
  }, [
    isLoading,
    viewType,
    activeSubscriptions,
    disabledSubscriptions,
    deletedSubscriptions
  ])

  // Transform data for monthly view
  const monthlyChartData = useMemo(() => {
    if (isLoading || viewType !== "monthly") return null

    const monthlyData = groupSubscriptionCountByMonth(
      activeSubscriptions,
      disabledSubscriptions,
      deletedSubscriptions
    )

    // Build arrays for chart
    const categories = monthlyData.map(item => `${item.month} ${item.year}`)
    const activeData = monthlyData.map(item => item.activeCount)
    const disabledData = monthlyData.map(item => item.disabledCount)
    const deletedData = monthlyData.map(item => item.deletedCount)
    const periods = monthlyData.map(item => `${item.year}-${item.month}`)

    return {
      categories,
      activeData,
      disabledData,
      deletedData,
      periods // Store year-month strings like "2025-January"
    }
  }, [
    isLoading,
    viewType,
    activeSubscriptions,
    disabledSubscriptions,
    deletedSubscriptions
  ])

  // Get chart data based on view type
  const chartData = viewType === "daily" ? dailyChartData : monthlyChartData

  // Handle chart bar click
  const handlePointClick = useCallback(
    (index: number, seriesName: string) => {
      if (!chartData || !onChartClick) return

      const period = chartData.periods[index]
      const category = chartData.categories[index]

      let status: "active" | "disabled" | "deleted" = "active"
      if (seriesName === "Active") status = "active"
      else if (seriesName === "Disabled") status = "disabled"
      else if (seriesName === "Deleted") status = "deleted"

      onChartClick({
        period,
        viewType,
        status,
        category,
        index
      })
    },
    [chartData, onChartClick, viewType]
  )

  // Generate chart options
  const chartOptions = useMemo(() => {
    if (!chartData) return null

    const series: ChartSeries[] = [
      {
        name: "Active",
        data: chartData.activeData,
        color: "#28B85E" // Green
      },
      {
        name: "Disabled",
        data: chartData.disabledData,
        color: "#EBC230" // Yellow
      },
      {
        name: "Deleted",
        data: chartData.deletedData,
        color: "#DD524C" // Red
      }
    ]

    return getStackedColumnChartOptions(
      chartData.categories,
      series,
      viewType,
      handlePointClick,
      selectedBarIndex
    )
  }, [chartData, viewType, selectedBarIndex, handlePointClick])

  const hasData =
    chartData &&
    (chartData.activeData.some(v => v > 0) ||
      chartData.disabledData.some(v => v > 0) ||
      chartData.deletedData.some(v => v > 0))

  return (
    <div className="flex flex-col flex-1 mt-2 mb-4 overflow-hidden">
      <Accordion
        title="Total Subscriptions"
        childrenClassName="px-6 pb-4"
        noBorder={true}
      >
        {/* View Toggle Buttons */}
        <div className="flex gap-2 mt-4 mb-4">
          <button
            type="button"
            onClick={() => setViewType("daily")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === "daily"
                ? "bg-primary-alt-400 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Daily
          </button>
          <button
            type="button"
            onClick={() => setViewType("monthly")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === "monthly"
                ? "bg-primary-alt-400 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Monthly
          </button>
        </div>

        {/* Chart Container */}
        <div className="bg-white rounded-md relative">
          {isLoading && (
            <div className="flex justify-center items-center py-20">
              <Loader2
                className="animate-spin h-12 w-12 text-primary-alt-400"
                aria-hidden="true"
              />
            </div>
          )}

          {!isLoading && !hasData && (
            <div className="flex justify-center items-center py-20">
              <p className="text-gray-500 text-sm">No data available</p>
            </div>
          )}

          {!isLoading && hasData && chartOptions && (
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          )}
        </div>
      </Accordion>
    </div>
  )
}

export default SubscriptionStats
